/**
 * ハイライトリールのシーン構成ロジック
 *
 * 全写真を等間隔で流すのではなく、旅を「オープニング → スポットごとの章 → エンディング」に
 * 再構成し、各章では連写を間引いたベストショットだけを見せる。
 */

import type { ImmichAsset } from '@/api/services/immich'
import type { TripDay, TripDetail, TripRoutePoint, TripVisit } from '@/api/types/trip'
import { calculateDistance } from '@/lib/geo-utils'
import { stripQueryParams } from '@/lib/utils'

import { tripPlaceName } from './trip-utils'

/** この間隔以内に撮られた写真は同じ被写体の連写とみなす */
const BURST_WINDOW_MS = 90_000
/** 1 スポットあたりに採用する最大枚数 */
const MAX_SHOTS_PER_CHAPTER = 4
/** 訪問に紐付かない「移動中」写真の 1 日あたり最大枚数 */
const MAX_SHOTS_PER_TRANSIT = 2

export type SceneKind = 'opening' | 'chapter' | 'photo' | 'ending'

type BaseScene = {
  /** 表示時間 */
  durationMs: number
}

export type OpeningScene = BaseScene & {
  kind: 'opening'
  title: string
  dateRange: string
  duration: string
  prefectures: string[]
  coverAssetId: string | null
}

export type ChapterScene = BaseScene & {
  kind: 'chapter'
  dayNumber: number
  date: string
  /** 「東大寺」「移動中」 */
  placeName: string
  /** 「1時間20分滞在」など。算出できなければ null */
  stayLabel: string | null
  photoCount: number
  steps: number | null
  position: google.maps.LatLngLiteral | null
  /** 直前のスポット（旅の最初は null） */
  fromPosition: google.maps.LatLngLiteral | null
  fromPlaceName: string | null
  /** 到着地マーカーに図として出す代表写真 */
  photoAssetId: string | null
  /** Place に登録された写真の署名対象パス */
  placeImagePath: string | null
  /** 直前のスポットからの移動軌跡 */
  legPath: google.maps.LatLngLiteral[]
  /** 直前のスポットからの移動距離 */
  legDistanceKm: number | null
}

export type PhotoScene = BaseScene & {
  kind: 'photo'
  asset: ImmichAsset
  dayNumber: number
  date: string
  placeName: string | null
  position: google.maps.LatLngLiteral | null
  /** 撮影地点の標高（m） */
  altitude: number | null
}

export type EndingScene = BaseScene & {
  kind: 'ending'
  title: string
  totalSteps: number
  travelDistanceKm: number
  placeCount: number
  newPlaceCount: number
  maxAltitudeM: number | null
  photoCount: number
  prefectures: string[]
}

export type Scene = OpeningScene | ChapterScene | PhotoScene | EndingScene

const SCENE_DURATION: Record<SceneKind, number> = {
  opening: 4000,
  // 地図が出発地から目的地へ寄っていく尺を確保する
  chapter: 4200,
  photo: 3400,
  ending: 6000,
}

const byCreatedAt = (a: ImmichAsset, b: ImmichAsset) => a.fileCreatedAt.localeCompare(b.fileCreatedAt)

/**
 * 連写を 1 枚に畳んでから、上限枚数まで等間隔に間引く。
 * 連写の代表は動画を優先し、なければ塊の中央（撮り直しの最終形に近い）を採る。
 */
export const pickBestShots = (assets: ImmichAsset[], limit: number): ImmichAsset[] => {
  if (assets.length === 0) return []

  const sorted = [...assets].sort(byCreatedAt)
  const bursts: ImmichAsset[][] = []

  for (const asset of sorted) {
    const currentBurst = bursts.at(-1)
    const head = currentBurst?.at(0)
    const isSameBurst =
      head && new Date(asset.fileCreatedAt).getTime() - new Date(head.fileCreatedAt).getTime() < BURST_WINDOW_MS

    if (currentBurst && isSameBurst) {
      currentBurst.push(asset)
      continue
    }
    bursts.push([asset])
  }

  const representatives = bursts.map(
    (burst) => burst.find((a) => a.type === 'VIDEO') ?? burst.at(Math.floor(burst.length / 2)) ?? burst[0],
  )

  if (representatives.length <= limit) return representatives

  const stride = representatives.length / limit
  return Array.from({ length: limit }, (_, i) => representatives.at(Math.floor(i * stride))).filter(
    (a): a is ImmichAsset => a !== undefined,
  )
}

/** 撮影時刻に最も近い軌跡点の標高を引く（軌跡は時系列前提） */
const findAltitude = (points: TripRoutePoint[], isoTime: string): number | null => {
  if (points.length === 0) return null

  const target = new Date(isoTime).getTime()
  let nearest: TripRoutePoint | null = null
  let nearestDiff = Number.POSITIVE_INFINITY

  for (const point of points) {
    const diff = Math.abs(new Date(point.timestamp).getTime() - target)
    // 時系列なので差が開き始めたら以降は遠ざかる一方
    if (diff > nearestDiff) break
    nearestDiff = diff
    nearest = point
  }

  // 30 分以上離れた点の標高は撮影地のものとみなさない
  if (!nearest || nearestDiff > 30 * 60 * 1000) return null
  return nearest.altitude
}

/** 「1時間20分」「45分」 */
const stayLabel = (visit: TripVisit): string | null => {
  if (!visit.arrival_date || !visit.departure_date) return null

  const minutes = Math.round(
    (new Date(visit.departure_date).getTime() - new Date(visit.arrival_date).getTime()) / 60_000,
  )
  if (minutes < 1) return null
  if (minutes < 60) return `${minutes}分滞在`

  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${hours}時間滞在` : `${hours}時間${rest}分滞在`
}

const visitPosition = (visit: TripVisit): google.maps.LatLngLiteral => ({
  lat: visit.latitude,
  lng: visit.longitude,
})

/** 署名 API に渡すパスに揃える（クエリと /api プレフィックスを剥がす） */
const placeImagePath = (visit: TripVisit): string | null => {
  const url = visit.place?.images.at(0)?.url
  if (!url) return null
  const path = stripQueryParams(url)
  return path.startsWith('/api') ? path.slice(4) : path
}

/** これより近い連続点は同じ地点とみなして間引く */
const MIN_LEG_STEP_KM = 0.01

/** 指定区間の GPS 点を時系列で抜き出す */
const legPoints = (points: TripRoutePoint[], fromIso: string | null, toIso: string): google.maps.LatLngLiteral[] => {
  if (!fromIso) return []

  const from = new Date(fromIso).getTime()
  const to = new Date(toIso).getTime()
  if (to <= from) return []

  return points
    .filter((p) => {
      const t = new Date(p.timestamp).getTime()
      return t >= from && t <= to
    })
    .map((p) => ({ lat: p.latitude, lng: p.longitude }))
}

const dedupePath = (path: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] => {
  const result: google.maps.LatLngLiteral[] = []
  for (const point of path) {
    const last = result.at(-1)
    if (last && calculateDistance(last.lat, last.lng, point.lat, point.lng) < MIN_LEG_STEP_KM) continue
    result.push(point)
  }
  return result
}

/**
 * 章の演出で見せる移動区間。
 * GPS 点の両端に出発地と到着地を繋ぐので、軌跡が欠けている区間だけが直線になる。
 */
const buildLeg = (
  points: TripRoutePoint[],
  fromIso: string | null,
  toIso: string,
  from: google.maps.LatLngLiteral | null,
  to: google.maps.LatLngLiteral,
): google.maps.LatLngLiteral[] => {
  if (!from) return []
  return dedupePath([from, ...legPoints(points, fromIso, toIso), to])
}

const pathDistanceKm = (path: google.maps.LatLngLiteral[]): number | null => {
  if (path.length < 2) return null

  let total = 0
  for (let i = 1; i < path.length; i += 1) {
    const a = path.at(i - 1)
    const b = path.at(i)
    if (!a || !b) continue
    total += calculateDistance(a.lat, a.lng, b.lat, b.lng)
  }
  return total
}

type BuildArgs = {
  trip: TripDetail
  days: TripDay[]
  routePoints: TripRoutePoint[]
  /** オープニングに使う日程ラベル */
  dateRange: string
  duration: string
}

/** 旅をダイジェスト映像のシーン列に組み立てる */
export const buildScenes = ({ trip, days, routePoints, dateRange, duration }: BuildArgs): Scene[] => {
  const scenes: Scene[] = []

  const pushPhotos = (
    assets: ImmichAsset[],
    context: Pick<PhotoScene, 'dayNumber' | 'date' | 'placeName' | 'position'>,
  ) => {
    for (const asset of assets) {
      scenes.push({
        kind: 'photo',
        durationMs: SCENE_DURATION.photo,
        asset,
        altitude: findAltitude(routePoints, asset.fileCreatedAt),
        ...context,
      })
    }
  }

  // 章をまたいで「どこから来たか」を繋ぐ
  let previousPosition: google.maps.LatLngLiteral | null = null
  let previousPlaceName: string | null = null
  let previousTimestamp: string | null = null

  days.forEach((day, dayIndex) => {
    const dayNumber = dayIndex + 1

    for (const visit of day.visits) {
      // 写真が無くても章は残す。地図と滞在情報だけでも旅の軌跡は繋がる
      const shots = pickBestShots(visit.photos, MAX_SHOTS_PER_CHAPTER)

      const placeName = tripPlaceName(visit, '名もなき場所')
      const position = visitPosition(visit)
      const arrival = visit.arrival_date ?? visit.timestamp
      const legPath = buildLeg(routePoints, previousTimestamp, arrival, previousPosition, position)

      scenes.push({
        kind: 'chapter',
        durationMs: SCENE_DURATION.chapter,
        dayNumber,
        date: day.date,
        placeName,
        stayLabel: stayLabel(visit),
        photoCount: visit.photos.length,
        steps: day.steps || null,
        position,
        fromPosition: previousPosition,
        fromPlaceName: previousPlaceName,
        photoAssetId: shots.at(0)?.id ?? null,
        placeImagePath: placeImagePath(visit),
        legPath,
        legDistanceKm: pathDistanceKm(legPath),
      })
      pushPhotos(shots, { dayNumber, date: day.date, placeName, position })

      previousPosition = position
      previousPlaceName = placeName
      previousTimestamp = visit.departure_date ?? arrival
    }

    // 移動中に撮った写真は章カードなしで挟み、道中の空気だけ残す
    pushPhotos(pickBestShots(day.photos, MAX_SHOTS_PER_TRANSIT), {
      dayNumber,
      date: day.date,
      placeName: null,
      position: null,
    })
  })

  // 訪問も写真も無い旅ではダイジェストが成立しない
  if (scenes.length === 0) return []

  const { stats } = trip

  scenes.unshift({
    kind: 'opening',
    durationMs: SCENE_DURATION.opening,
    title: trip.title,
    dateRange,
    duration,
    prefectures: stats.prefectures,
    coverAssetId: trip.cover_asset_id,
  })

  scenes.push({
    kind: 'ending',
    durationMs: SCENE_DURATION.ending,
    title: trip.title,
    totalSteps: stats.total_steps,
    travelDistanceKm: stats.travel_distance_km,
    placeCount: stats.place_count,
    newPlaceCount: stats.new_place_count,
    maxAltitudeM: stats.max_altitude_m,
    photoCount: stats.photo_count + stats.video_count,
    prefectures: stats.prefectures,
  })

  return scenes
}
